import type { DefaultRoundSettings } from "Models";
import type { DefaultRoundSettingsUpdateBody, UseDataInfo } from "QueryTypes";
import { useSelector } from "react-redux";
import type { QueryState } from "redux-query";
import { type RunMutation, useMutation, useRequest } from "redux-query-react";
import type { EntitiesState, RootState } from "StoreTypes";
import { getFacilityDefaultRoundSettings } from "./defaultRoundSettingsSelectors";
import { useMemo } from "react";
import {
  facilityDefaultRoundSettingsQuery,
  makeUpdateDefaultRoundSettingsMutation,
} from "./defaultRoundSettingsQueries";

export const useUpdateDefaultRoundSettings = (
  facilityId: string,
): [
  QueryState,
  RunMutation<EntitiesState, [DefaultRoundSettingsUpdateBody]>,
] => {
  const [updateDefaultRoundSettingsQueryState, updateDefaultRoundSettings] =
    useMutation((body: DefaultRoundSettingsUpdateBody) =>
      makeUpdateDefaultRoundSettingsMutation(facilityId, body),
    );

  return [updateDefaultRoundSettingsQueryState, updateDefaultRoundSettings];
};

export const useFacilityDefaultRoundSettings = (
  facilityId: string,
): UseDataInfo<DefaultRoundSettings | undefined> => {
  const safeQuery = useMemo(() => {
    if (!facilityId?.trim()) {
      console.log(
        "facilityId is falsy or empty for default round settings query",
      );
      return null;
    }
    return facilityDefaultRoundSettingsQuery(facilityId);
  }, [facilityId]);

  const [queryState, refreshFacilityDefaultRoundSettings] = useRequest(
    safeQuery ?? undefined,
  );

  const facilityDefaultRoundSettings = useSelector<
    RootState,
    DefaultRoundSettings | undefined
  >((state: RootState) => getFacilityDefaultRoundSettings(state, facilityId));

  return [
    facilityDefaultRoundSettings,
    queryState,
    refreshFacilityDefaultRoundSettings,
  ];
};
